import { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router";

import _ from "lodash";
import { Contained } from "src/components/StyledComponents";
import { useAppDispatch } from "src/redux/hooks";
import {
  OrderVariantErrorType,
  TOrderVariantError,
  setClearAllErrors,
} from "src/redux/slices/ordering/orderSetSlice";
import { formatMoneyString } from "src/utility/utilityFunctions";

import { Button, FaIcon, PaperTooltip } from "@features/ui";

import { getExtendedCost } from "../helpers";
import UploadOrdersButtons from "./UploadOrdersButtons";
import { useCurrentOrderSet } from "./data/CurrentOrderSetContext";
import useDeleteAllOrdersMutation from "./data/mutations/useDeleteAllOrdersMutation";
import useDeleteOrderSetMutation from "./data/mutations/useDeleteOrderSetMutation";

const OrderSetActionBar = () => {
  const { hash } = useLocation();
  const orderVariantErrors: Record<string, TOrderVariantError> = useSelector(
    (state: any) => state.orderSet.orderVariantErrors
  );
  const hasOrderVariantErrors = Object.keys(orderVariantErrors).length > 0;
  const deleteOrderSet = useDeleteOrderSetMutation();
  const deleteAllOrders = useDeleteAllOrdersMutation();
  const { orderSet, orders, isCoopOrderSet } = useCurrentOrderSet();

  let orderSetExtendedTotal = _.sumBy(orders, getExtendedCost);
  if (isCoopOrderSet) {
    orderSetExtendedTotal = orderSetExtendedTotal / 2;
  }

  const hasQuantities = orders.some((order) => order.totalQuantity > 0);
  const canDelete = orderSet.status !== "approved";

  const handleRemoveAllOrders = () => {
    deleteAllOrders.mutate();
  };
  return (
    <div className="fixed inset-0 top-auto bottom-4 z-20 left-[var(--sidebar-width)]">
      <Contained>
        <div className="rounded shadow-md bg-white flex justify-between items-center px-4 py-3">
          <div className="flex justify-end items-baseline gap-4">
            {canDelete && (
              <>
                <Button
                  variant="danger"
                  onClick={() => deleteOrderSet.mutate()}
                >
                  Delete Order Set
                </Button>
                <Button variant="danger" onClick={handleRemoveAllOrders}>
                  Delete All Orders
                </Button>
              </>
            )}
          </div>
          <div className="flex justify-end items-center gap-4">
            {orders.length > 0 ? (
              <>
                {!hasOrderVariantErrors ? (
                  <h2 className="text-neutral-600 text-lg">
                    Total:{" "}
                    <b className="font-medium">
                      {formatMoneyString(orderSetExtendedTotal)}
                    </b>
                  </h2>
                ) : (
                  <OrderVariantErrorsSummary errors={orderVariantErrors} />
                )}
              </>
            ) : (
              <UploadOrdersButtons />
            )}
            <Button
              component={Link}
              disabled={
                orderSet.status === "inactive" ||
                !hasQuantities ||
                Object.keys(orderVariantErrors).length !== 0
              }
              variant="contained"
              to={hash ? hash + "-overview" : "#overview"}
            >
              Order Overview
            </Button>
          </div>
        </div>
      </Contained>
    </div>
  );
};

const OrderVariantErrorsSummary = ({
  errors,
}: {
  errors: Record<string, TOrderVariantError>;
}) => {
  const dispatch = useAppDispatch();
  const [popperOpen, setPopperOpen] = useState(false);
  const l = Object.keys(errors).length;
  const goToCellId = (id: string) => {
    setPopperOpen(false);
    const cell = document.getElementById(id);
    if (cell) {
      cell.focus();
      cell.scrollIntoView({ behavior: "smooth" });
    }
  };
  // TODO: revisit this when we have distributor budgets
  const shortErrorMsg = (error: TOrderVariantError) =>
    error.type === OrderVariantErrorType.OVER_BUDGET ? (
      <>
        Requested quantity would exceed budget for{" "}
        <b className="font-medium">{error.error.split(" is")[0]}</b>
      </>
    ) : (
      error.error
    );
  return (
    <PaperTooltip
      open={popperOpen}
      onOpen={() => setPopperOpen(true)}
      onClose={() => setPopperOpen(false)}
      classes={{ tooltip: "max-w-sm" }}
      arrow
      title={
        <div className="w-full max-w-none">
          <p className="mb-2">Fix the following errors to continue:</p>
          <ul className="list-disc list-outside ml-4 space-y-2">
            {Object.keys(errors).map((id) => (
              <li key={id}>
                {shortErrorMsg(errors[id])}{" "}
                <button
                  onClick={() => goToCellId(id)}
                  className="text-primary-500 bg-neutral-100 px-2 py-px rounded-md"
                >
                  <FaIcon icon="arrow-turn-down-right" />
                </button>
              </li>
            ))}
          </ul>
          <Button
            variant="contained"
            fullWidth
            color="secondary"
            onClick={() => {
              dispatch(setClearAllErrors(true));
            }}
            className="mt-4"
          >
            Reset quantities and clear errors
          </Button>
        </div>
      }
    >
      <div className="underline underline-offset-2 text-red-600 cursor-help px-1">
        {l} error{l > 1 ? "s" : ""} pending
      </div>
    </PaperTooltip>
  );
};

export default OrderSetActionBar;
